import React from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import Box from 'sail/Box';
import {AlignBox} from 'sail/Align';
import Badge from 'sail/Badge';
import Card from 'sail/Card';
import Group from 'sail/Group';
import Select from 'sail/Select';
import {InlineLink} from 'sail/InlineLink';
import {Title, Intro, Text, Body, DisplayTextProps} from 'sail/Text';
import Toggle, {ToggleItem} from 'sail/Toggle';
import {CompactTooltip} from 'sail/Tooltip';
import {AttentionNotice} from 'sail/Notice';

import {useLocation} from 'docs/data';
import {Feedback, Heading} from 'docs/components';
import StepList from './left-col/StepList';
import Extension from './left-col/Extension';
import CodeView from './right-col/Code';
import Settings from './Settings';
import NarrowScreenWarning from './NarrowScreenWarning';
import {DownloadButton} from './DownloadButton';
import {useGuide} from '../data/GuideProvider';
import {useContent} from '../data/ContentProvider';
import {FormattedMessageOrString} from '../../../data/localization/intlUtils';

import '../css/Guide.scss';

const messages = defineMessages({
  guide: {
    id: 'docsShell.integrationBuilder.guide.guide',
    description:
      'Callout to read a text-version of the guide with a link to the guide',
    defaultMessage: 'View the <GuideLink>text-based guide</GuideLink>.',
  },
  guideText: {
    id: 'docsShell.integrationBuilder.guide.guideText',
    description:
      'Callout to read a text-version of the guide with a link to the guide',
    defaultMessage: 'View the text-based guide',
  },
  partners: {
    id: 'docsShell.integrationBuilder.guide.partners',
    description: 'Callout to go to the partners program page.',
    defaultMessage:
      "Don't code? Use Stripe’s <NoCodeLink>no-code options</NoCodeLink> or get help from <PartnersLink>our partners</PartnersLink>.",
  },
  text: {
    id: 'docsShell.integrationBuilder.guide.text',
    description: 'Callout to read a text-version of the guide',
    defaultMessage: 'Read the text-based version of this guide',
  },
  support: {
    id: 'docsShell.integrationBuilder.guide.support',
    description: 'Callout to contact support',
    defaultMessage: 'Need more help? Contact support.',
  },
  checkoutLabel: {
    id: 'docsShell.integrationBuilder.guide.checkoutLabel',
    description: 'Toggle button label',
    defaultMessage: 'Prebuilt Checkout page',
  },
  paymentsLabel: {
    id: 'docsShell.integrationBuilder.guide.paymentsLabel',
    description: 'Toggle button label',
    defaultMessage: 'Custom payment flow',
  },
  paymentsCallout: {
    id: 'docsShell.integrationBuilder.guide.paymentCallout',
    description: 'Callout to notify the removal of the docs page',
    defaultMessage:
      'We recommend using the Payment Element instead of the Card Element. Get started with the <a>Payment Element integration builder</a>.',
  },
});

const NextStep = ({nextStep}) => (
  <Group>
    <Heading level={3}>
      <InlineLink
        href={nextStep.href}
        style={{fontWeight: 700}}
        target="_blank"
      >
        <FormattedMessageOrString stringOrDefineMessage={nextStep.heading} />
      </InlineLink>
    </Heading>
    <Intro>
      <FormattedMessageOrString stringOrDefineMessage={nextStep.body} />
    </Intro>
  </Group>
);

const IntegrationExtension = () => {
  const {activeStep, options} = useGuide();
  const {steps, extensions, copy} = useContent();

  const extensionSteps = steps.filter(
    (s) => s.extension === 'extra' && (!s.filter || s.filter(options)),
  );
  const bottomCopy = copy.bottom || {};
  return (
    <Box margin={{horizontal: 32}}>
      <Group spacing={{vertical: 8}} margin={{bottom: 16}}>
        <Text style={{fontWeight: 700}} size={20} lineHeight={40} color="dark">
          <FormattedMessageOrString
            stringOrDefineMessage={bottomCopy.heading}
          />
        </Text>
        <Intro
          style={{
            marginTop: '8px',
          }}
          color="default"
        >
          <FormattedMessageOrString stringOrDefineMessage={bottomCopy.body} />
        </Intro>
      </Group>
      {extensionSteps.map((extension, i) => {
        return (
          <Extension
            key={`extension-${i}`}
            extension={extension}
            steps={steps.filter((s) => s.extensionId === extension.id)}
            open={extensions[extension.id]}
            active={activeStep && activeStep.id === extension.id}
          />
        );
      })}
      <Group spacing={20} margin={{bottom: 48, top: 48}}>
        <Title color="dark" style={{fontWeight: '600'}}>
          <FormattedMessageOrString
            stringOrDefineMessage={bottomCopy.subheading}
          />
        </Title>
        {bottomCopy.nextSteps.map((nextStep, i) => (
          <NextStep key={`nextStep-${i}`} nextStep={nextStep} />
        ))}
      </Group>
    </Box>
  );
};

const CheckoutElementsToggle = () => {
  const {location, pushLocation} = useLocation();
  const intl = useIntl();

  const showCheckout = location.integrationBuilderType() === 'checkout';
  const checkoutLabel = intl.formatMessage(messages.checkoutLabel);
  const paymentsLabel = intl.formatMessage(messages.paymentsLabel);

  const switchIntegration = (integrationType) => {
    if (integrationType === 'payments') {
      pushLocation(`/docs/${integrationType}/quickstart`);
    } else {
      pushLocation(`/docs/${integrationType}/integration-builder`);
    }
  };
  return (
    <Box margin={{top: 16}}>
      <Group direction="horizontal" spacing={8} className="Guide--toggle-full">
        <Toggle name="Guide--toggle" type="radio">
          <ToggleItem
            key="Guide--toggle--checkout"
            label={checkoutLabel}
            checked={showCheckout}
            onChange={() => {
              switchIntegration('checkout');
            }}
          />
          <ToggleItem
            key="Guide--toggle--elements"
            label={paymentsLabel}
            checked={!showCheckout}
            onChange={() => {
              switchIntegration('payments');
            }}
          />
        </Toggle>
      </Group>
      <Select
        className="Guide--toggle-small"
        value={showCheckout ? 'checkout' : 'payments'}
        width={200}
        onChange={(val) => {
          switchIntegration(val);
        }}
        options={[
          {
            value: 'checkout',
            label: checkoutLabel,
          },
          {
            value: 'payments',
            label: paymentsLabel,
          },
        ]}
      />
    </Box>
  );
};

const Introduction = () => {
  const {copy, integrationType} = useContent();

  return (
    <Box
      className="Introduction"
      flex={{direction: 'column', justifyContent: 'center'}}
      padding={{bottom: 32}}
      margin={{horizontal: 32}}
    >
      <header style={{paddingTop: 0}}>
        <Heading level={1} className="Title Introduction--title">
          <span>
            <FormattedMessageOrString stringOrDefineMessage={copy.heading} />
          </span>
          {copy.guideHref && (
            <span className="Introduction--title-icon">
              &nbsp;&nbsp;
              <AlignBox
                reference={DisplayTextProps}
                style={{display: 'inline-flex'}}
              >
                <InlineLink href={copy.guideHref} className="GuideBadge">
                  <CompactTooltip
                    context={
                      <Badge
                        icon="guide"
                        collapsed
                        collapseDirection="right"
                        label={<FormattedMessage {...messages.guideText} />}
                        style={{marginLeft: 0}}
                      />
                    }
                    label={<FormattedMessage {...messages.guideText} />}
                  />
                </InlineLink>
              </AlignBox>
            </span>
          )}
        </Heading>
        {(integrationType === 'payments' || integrationType === 'checkout') && (
          <CheckoutElementsToggle />
        )}
        <Box margin={{bottom: 32, top: 20}}>
          <Text className="Guide--description" size={16} lineHeight={24}>
            {integrationType === 'payments' && (
              <Box padding={{bottom: 12}}>
                <Card className="Callout" shadow="keyline">
                  <AttentionNotice
                    title="Attention"
                    description={
                      <FormattedMessage
                        {...messages.paymentsCallout}
                        values={{
                          a: (str) => (
                            <a href="/docs/payments/quickstart">{str}</a>
                          ),
                        }}
                      />
                    }
                  />
                </Card>
              </Box>
            )}
            <FormattedMessageOrString stringOrDefineMessage={copy.body} />
          </Text>
        </Box>
        <Group
          direction="horizontal"
          alignItems="center"
          spacing={12}
          wrap="wrap"
        >
          <DownloadButton />
          <Text color="gray">
            <FormattedMessage
              {...messages.partners}
              values={{
                PartnersLink: (label) => (
                  <InlineLink
                    href="/partners/apps-and-extensions"
                    target="_blank"
                  >
                    {label}
                  </InlineLink>
                ),
                NoCodeLink: (text) => (
                  <InlineLink href="/docs/payments/no-code" target="_blank">
                    {text}
                  </InlineLink>
                ),
              }}
            />
          </Text>
        </Group>
      </header>
    </Box>
  );
};

const FooterLinks = () => {
  const {copy} = useContent();
  const {guideHref} = copy;
  return (
    <Box margin={{horizontal: 32}}>
      <Group spacing={4}>
        {guideHref && (
          <Body display="block">
            <InlineLink href={guideHref}>
              <FormattedMessage {...messages.text} />
            </InlineLink>
          </Body>
        )}
        <Body display="block">
          <InlineLink
            href="https://support.stripe.com/?contact=true"
            target="_blank"
          >
            <FormattedMessage {...messages.support} />
          </InlineLink>
        </Body>
      </Group>
    </Box>
  );
};

const Guide = () => {
  const {merchant, fireEvent} = useGuide();
  const {sections} = useContent();

  if (!sections) {
    return null;
  }
  return (
    <>
      <NarrowScreenWarning />
      <Settings />
      <Box flex={{direction: 'row'}}>
        <Box
          flex={{direction: 'column'}}
          className="Guide"
          style={{height: '100%'}}
        >
          <Box flex>
            <Box className="Guide-leftcol">
              <Introduction />
              {sections.map((section, i) => (
                <StepList key={`section-${i}`} section={section} index={i} />
              ))}
              <IntegrationExtension />
              <Group style={{marginBottom: '128px'}} spacing={20}>
                <Feedback merchant={merchant} feedbackHandler={fireEvent} />
                <FooterLinks />
              </Group>
            </Box>
            <Box className="Guide-rightcol">
              <CodeView />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Guide;
